import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '@/lib/apiClient';
import { useAuthStore } from '@/stores/authStore';

/**
 * Browser-session idle logout (as opposed to JWT TTL, which is purely
 * cryptographic). When the user has not produced mouse / keyboard /
 * touch / focus / scroll activity for [idleTimeoutMinutes], we clear
 * tokens, redirect to /login, and let them log back in.
 *
 * Activity events are passive listeners on the window so the cost is
 * negligible. We debounce by storing the last-activity timestamp in a
 * ref and checking it on a 30-second interval — far cheaper than
 * resetting a setTimeout on every event (which fires hundreds of times
 * per minute when a user is actively scrolling).
 *
 * idleTimeoutMinutes = 0 disables the hook entirely (only the JWT TTL
 * limits the session). Useful for users who explicitly want a
 * "remember me forever" experience.
 *
 * Cross-tab: the activity ref is per-tab. Two tabs of the same browser
 * track independently. That's intentional — a user reading on one
 * tab is "active" on that tab, even if another tab is idle.
 */
export function useIdleLogout(idleTimeoutMinutes: number) {
  const navigate = useNavigate();
  const lastActivity = useRef<number>(Date.now());
  const isAuthed = useAuthStore((s) => s.tokens !== null);

  useEffect(() => {
    if (!isAuthed) return;
    if (idleTimeoutMinutes <= 0) return; // disabled

    const onActivity = () => {
      lastActivity.current = Date.now();
    };

    const events: Array<keyof WindowEventMap> = [
      'mousemove',
      'mousedown',
      'keydown',
      'wheel',
      'touchstart',
      'scroll',
      'focus',
    ];
    events.forEach((e) => window.addEventListener(e, onActivity, { passive: true }));

    const timeoutMs = idleTimeoutMinutes * 60_000;
    const checkEveryMs = Math.min(30_000, Math.max(5_000, Math.floor(timeoutMs / 6)));

    const intervalId = window.setInterval(() => {
      const idleFor = Date.now() - lastActivity.current;
      if (idleFor >= timeoutMs) {
        // Server-side: revoke the refresh token so it can't be reused.
        // Best-effort — if it fails (e.g. network), we still clear local
        // state and force re-login.
        const tokens = useAuthStore.getState().tokens;
        if (tokens?.refreshToken) {
          apiClient
            .post('/auth/logout', { refreshToken: tokens.refreshToken })
            .catch(() => {});
        }
        useAuthStore.getState().clear();
        navigate('/login?reason=idle', { replace: true });
      }
    }, checkEveryMs);

    return () => {
      events.forEach((e) => window.removeEventListener(e, onActivity));
      window.clearInterval(intervalId);
    };
  }, [idleTimeoutMinutes, isAuthed, navigate]);
}
